import React, { useCallback, useState } from 'react';

import { Empty } from 'antd';
import dayjs from 'dayjs';

import { matchesKeywordFilter } from '@/shared/lib/text';
import type { BadgeItem } from '@/shared/types/badge';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { EllipsisText } from '@/shared/ui/EllipsisText';

import { downloadAssetData } from '@/entities/asset-search/api/downloadAsset';
import type { AssetDetailResponse } from '@/entities/asset-search/domain/types';

import { ColumnListScrollBlock } from '@/widgets/experiment-asset-detail/ColumnListScrollBlock/ColumnListScrollBlock';
import { ExperimentAssetTabShell } from '@/widgets/experiment-asset-detail/ExperimentAssetTabShell';

import styles from './FeatureSetTab.module.css';

type FeatureSetTabProps = {
  featureSet: AssetDetailResponse['featureSet'];
  isCurrentAssetType: boolean;
};

export const FeatureSetTab: React.FC<FeatureSetTabProps> = ({ featureSet, isCurrentAssetType }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!featureSet?.id) return;

    setIsDownloading(true);
    try {
      await downloadAssetData('feature_set', featureSet.id, featureSet.snapshotId, featureSet.table);
    } catch (error) {
      console.error('[FeatureSetTab] Download error:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [featureSet]);

  if (!featureSet) {
    return (
      <ExperimentAssetTabShell variant={isCurrentAssetType ? 'sidebar' : 'panel'}>
        {() => (
          <div className={styles.emptyState}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="피처셋 정보가 없습니다" />
          </div>
        )}
      </ExperimentAssetTabShell>
    );
  }

  if (isCurrentAssetType) {
    return (
      <ExperimentAssetTabShell variant="sidebar">
        {({ searchQuery }) => {
          const filteredColumns = featureSet.columns.filter((col) => matchesKeywordFilter(searchQuery, col.name));
          const showSearchEmpty = searchQuery.trim().length > 0 && filteredColumns.length === 0;

          return (
            <>
              <div className={styles.sidebarInfoSection}>
                <div className={styles.sidebarInfoRow}>
                  <span className={styles.sidebarInfoLabel}>
                    <EllipsisText text="소유자" tooltipPlacement="topLeft">
                      소유자
                    </EllipsisText>
                  </span>
                  <span className={styles.sidebarInfoValue}>
                    <EllipsisText text={featureSet.creator || '-'} tooltipPlacement="topRight">
                      {featureSet.creator || '-'}
                    </EllipsisText>
                  </span>
                </div>
                <div className={styles.sidebarInfoRow}>
                  <span className={styles.sidebarInfoLabel}>
                    <EllipsisText text="생성일" tooltipPlacement="topLeft">
                      생성일
                    </EllipsisText>
                  </span>
                  <span className={styles.sidebarInfoValue}>
                    <EllipsisText
                      text={featureSet.createdAt ? dayjs(featureSet.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                      tooltipPlacement="topRight">
                      {featureSet.createdAt ? dayjs(featureSet.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                    </EllipsisText>
                  </span>
                </div>
                <div className={styles.sidebarInfoRow}>
                  <span className={styles.sidebarInfoLabel}>
                    <EllipsisText text="수정일" tooltipPlacement="topLeft">
                      수정일
                    </EllipsisText>
                  </span>
                  <span className={styles.sidebarInfoValue}>
                    <EllipsisText
                      text={featureSet.updatedAt ? dayjs(featureSet.updatedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                      tooltipPlacement="topRight">
                      {featureSet.updatedAt ? dayjs(featureSet.updatedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                    </EllipsisText>
                  </span>
                </div>
              </div>

              {showSearchEmpty ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="검색 결과가 없습니다" />
              ) : (
                <>
                  {filteredColumns.length > 0 && (
                    <div className={styles.detailStackSidebar}>
                      <ColumnListScrollBlock
                        highlightQuery={searchQuery}
                        items={filteredColumns.map(
                          (col): BadgeItem => ({
                            name: col.name,
                            tagFQN: col.name,
                          })
                        )}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          );
        }}
      </ExperimentAssetTabShell>
    );
  }

  return (
    <ExperimentAssetTabShell
      variant="panel"
      onDownload={featureSet?.id ? handleDownload : undefined}
      downloadDisabled={isDownloading || !featureSet?.id}>
      {({ searchQuery }) => {
        const filteredColumns = featureSet.columns.filter((col) => matchesKeywordFilter(searchQuery, col.name));
        const showDescription = !searchQuery.trim() || matchesKeywordFilter(searchQuery, featureSet.description ?? '');
        const hasSearchMatch = filteredColumns.length > 0 || (Boolean(featureSet.description) && showDescription);
        const showSearchEmpty = searchQuery.trim().length > 0 && !hasSearchMatch;

        return (
          <>
            <div className={styles.nameTitle}>
              <EllipsisText text={featureSet.table}>{featureSet.table}</EllipsisText>
            </div>

            {showDescription && featureSet.description && (
              <div className={styles.descriptionSection}>
                <AssetDescription
                  description={featureSet.description}
                  mode="expandable"
                  highlightQuery={searchQuery.trim()}
                />
              </div>
            )}

            <div className={styles.nameInfo}>
              <div className={styles.nameInfoRow}>
                <span className={styles.nameInfoLabel}>소유자</span>
                <span className={styles.nameInfoValue}>{featureSet.creator || '-'}</span>
              </div>
              <div className={styles.nameInfoRow}>
                <span className={styles.nameInfoLabel}>생성일</span>
                <span className={styles.nameInfoValue}>
                  {featureSet.createdAt ? dayjs(featureSet.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                </span>
              </div>
              <div className={styles.nameInfoRow}>
                <span className={styles.nameInfoLabel}>수정일</span>
                <span className={styles.nameInfoValue}>
                  {featureSet.updatedAt ? dayjs(featureSet.updatedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                </span>
              </div>
            </div>

            {showSearchEmpty ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="검색 결과가 없습니다" />
            ) : (
              <>
                {filteredColumns.length > 0 && (
                  <ColumnListScrollBlock
                    highlightQuery={searchQuery}
                    items={filteredColumns.map(
                      (col): BadgeItem => ({
                        name: col.name,
                        tagFQN: col.name,
                      })
                    )}
                  />
                )}
              </>
            )}
          </>
        );
      }}
    </ExperimentAssetTabShell>
  );
};
